import PropTypes from "prop-types";
import {useT} from "../i18n/i18n";
import {Link} from "react-router-dom";

export default function PrivacyPolicy({language}) {
    const {t} = useT(language);

    return (
        <section className="mx-auto max-w-4xl py-12 sm:py-16">
            <article className="prose prose-invert max-w-none">
                <h1 className="text-3xl font-semibold tracking-tight sm:text-4xl mb-2 text-[rgb(var(--app-fg))]">
                    {t("privacy.title")}
                </h1>
                <p className="text-sm text-[rgb(var(--app-muted))] mb-8">
                    {t("privacy.lastUpdated")}: June 27, 2026
                </p>

                <div className="space-y-8 text-[rgb(var(--app-muted))]">
                    {/* Introduction */}
                    <section>
                        <h2 className="text-xl font-semibold text-[rgb(var(--app-fg))] mb-3">
                            {t("privacy.sections.intro")}
                        </h2>
                        <p className="leading-relaxed">{t("privacy.introText")}</p>
                    </section>

                    {/* Cookie Policy */}
                    <section>
                        <h2 className="text-xl font-semibold text-[rgb(var(--app-fg))] mb-3">
                            {t("privacy.sections.cookiePolicy")}
                        </h2>
                        <p className="leading-relaxed mb-4">{t("privacy.cookiePolicyText")}</p>

                        <h3 className="text-lg font-medium text-[rgb(var(--app-fg))] mb-2">
                            {t("cookies.necessary")}
                        </h3>
                        <p className="leading-relaxed mb-4 pl-4 border-l-2 border-[rgb(var(--app-accent))]">
                            {t("privacy.necessaryText")}
                        </p>

                        <h3 className="text-lg font-medium text-[rgb(var(--app-fg))] mb-2">
                            {t("cookies.analytics")}
                        </h3>
                        <p className="leading-relaxed mb-4 pl-4 border-l-2 border-[rgb(var(--app-accent))]">
                            {t("privacy.analyticsText")}
                        </p>

                        <h3 className="text-lg font-medium text-[rgb(var(--app-fg))] mb-2">
                            {t("cookies.marketing")}
                        </h3>
                        <p className="leading-relaxed mb-4 pl-4 border-l-2 border-[rgb(var(--app-accent))]">
                            {t("privacy.marketingText")}
                        </p>

                        <p className="leading-relaxed bg-white/10 dark:bg-white/10 p-4 rounded-lg border border-white/20 backdrop-blur-sm text-white">
                            <span className="text-white/90">
                                {t("privacy.consentText")}
                            </span>
                        </p>
                    </section>

                    {/* Data Collection */}
                    <section>
                        <h2 className="text-xl font-semibold text-[rgb(var(--app-fg))] mb-3">
                            {t("privacy.sections.dataCollection")}
                        </h2>
                        <p className="leading-relaxed">{t("privacy.dataCollectionText")}</p>
                    </section>

                    {/* Use of Data */}
                    <section>
                        <h2 className="text-xl font-semibold text-[rgb(var(--app-fg))] mb-3">
                            {t("privacy.sections.dataUse")}
                        </h2>
                        <p className="leading-relaxed mb-4">{t("privacy.dataUseText")}</p>
                        <p className="leading-relaxed mb-4">
                            <strong className="text-[rgb(var(--app-fg))]">{t("privacy.lastUpdated")}:</strong>{" "}
                            {t("privacy.storageText")}
                        </p>
                        <p className="leading-relaxed">
                            <strong className="text-[rgb(var(--app-fg))]">{language === "en" ? "Third Parties" : "Terze Parti"}:</strong>{" "}
                            {t("privacy.thirdPartyText")}
                        </p>
                    </section>

                    {/* Your Rights (GDPR) */}
                    <section>
                        <h2 className="text-xl font-semibold text-[rgb(var(--app-fg))] mb-3">
                            {t("privacy.sections.dataRights")}
                        </h2>
                        <p className="leading-relaxed mb-4">{t("privacy.dataRightsText")}</p>
                        <div className="bg-white/10 dark:bg-white/10 p-5 rounded-lg border border-white/20 backdrop-blur-sm hover:bg-white/15 transition-colors">
                            <p className="leading-relaxed text-sm text-white">
                                <strong className="text-white block mb-2">
                                    {language === "en" ? "How to Exercise Your Rights:" : "Come Esercitare i Tuoi Diritti:"}
                                </strong>
                                {language === "en"
                                    ? "Send a request to the contact email below with details of your request. We will respond within 30 days."
                                    : "Invia una richiesta all'email di contatto sottostante con i dettagli della tua richiesta. Risponderemo entro 30 giorni."}
                            </p>
                        </div>
                    </section>

                    {/* GDPR Compliance */}
                    <section>
                        <h2 className="text-xl font-semibold text-[rgb(var(--app-fg))] mb-3">
                            {t("privacy.sections.gdpr")}
                        </h2>
                        <p className="leading-relaxed">{t("privacy.gdprText")}</p>
                    </section>

                    {/* Contact Us */}
                    <section>
                        <h2 className="text-xl font-semibold text-[rgb(var(--app-fg))] mb-3">
                            {t("privacy.sections.contact")}
                        </h2>
                        <p className="leading-relaxed mb-3">{t("privacy.contactUsText")}</p>
                        <div className="bg-white/10 dark:bg-white/10 p-5 rounded-lg border border-white/20 backdrop-blur-sm">
                            <p className="font-semibold text-white mb-3">Yurii Suprun</p>
                            <p className="text-white/90 flex items-center gap-2 mb-2">
                                <svg className="w-5 h-5 text-white/70 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
                                </svg>
                                <a href="mailto:iursuprun@gmail.com" className="hover:text-white transition-colors underline decoration-white/40 hover:decoration-white">
                                    iursuprun@gmail.com
                                </a>
                            </p>
                            <p className="text-sm text-white/80 flex items-center gap-2">
                                P.IVA (Italian Tax ID): 13728820963
                            </p>
                        </div>
                    </section>

                    {/* Links to Legal Documents */}
                    <section className="pt-6 border-t border-black/10 dark:border-white/15">
                        <p className="text-center text-sm">
                            <Link
                                to="/terms-of-service"
                                className="text-[rgb(var(--app-accent))] hover:underline"
                            >
                                {language === "en" ? "View Terms of Service" : "Visualizza i Termini di Servizio"}
                            </Link>
                        </p>
                    </section>
                </div>
            </article>
        </section>
    );
}

PrivacyPolicy.propTypes = {
    language: PropTypes.oneOf(["en", "it"]),
};
